import React, {useState, useEffect} from "react";
import {connect, useSelector} from "react-redux";
import Modal from "../common/Modal/Modal";
import {useSubmitGuard} from "../common/Helpers";
import {createQuote, sendQuote} from "../../actions/quote";
import {getCompanyPojects} from "../../actions/projects";

// A lighter clone of ContractCreateModal.js with the project-selection block removed entirely --
// a Quote is deliberately project-less and cold-sendable (see GitHub #624/#632/#633).
//
// Three actions, matching QuoteSingle.js's own Draft-editing section (isDraftForSender): Send
// Quote (create then immediately send, Draft -> Pending in one step -- the only option this form
// used to offer, see GitHub #656), Save as Draft (create only, leaving it in Draft for the Vendor
// to resume and send later from its own page), and Cancel (no write at all). A Recipient Email is
// only required to Send, not to Save as Draft -- matching QuoteViewSet.create()'s own lack of a
// recipient requirement (only send() requires one).
//
// Vendor-only by design: a Quote always carries a price/timeline the sender is proposing, which
// only makes sense coming from whoever would perform the work. A Purchaser wanting to solicit a
// price is a different, not-yet-built flow (see GitHub #652) -- this form doesn't offer that
// choice at all rather than let a Purchaser send a priced "quote" backwards.
export function QuoteCreateModal(props) {

    const [form, setForm] = useState({
        name: "",
        description: "",
        contract_value: "",
        start_date: "",
        completion_date: "",
        fk_email_recipient: "",
    });
    //GitHub #663: the Vendor pre-consenting to let the Purchaser Accept-and-Convert in one step --
    //separate from `form` since it drives a conditional project-select field, not a plain input
    const [allowConversion, setAllowConversion] = useState(false);
    const [conversionPid, setConversionPid] = useState("");

    const company_projects = useSelector((state) => state.projects.company_projects);

    useEffect(() => {
        props.getCompanyPojects();
    }, []);

    const {isSubmitting: isSending, guardedSubmit: guardSend} = useSubmitGuard();
    const {isSubmitting: isSavingDraft, guardedSubmit: guardSaveDraft} = useSubmitGuard();

    const handleChange = (e) => {
        setForm({...form, [e.target.name]: e.target.value});
    };

    const buildQuotePayload = () => ({
        company_creator_type: "Vendor",
        name: form.name,
        description: form.description || null,
        contract_value: form.contract_value || null,
        start_date: form.start_date || null,
        completion_date: form.completion_date || null,
        allow_contract_conversion: allowConversion,
        conversion_pid: allowConversion ? conversionPid : null,
    });

    const disableSend = !form.name || !form.fk_email_recipient || isSending || isSavingDraft;
    const disableSaveDraft = !form.name || (allowConversion && !conversionPid) || isSending || isSavingDraft;

    const handleSend = guardSend((e) => {
        e.preventDefault();
        return props.createQuote(buildQuotePayload()).then((createRes) => {
            if (createRes && createRes.data && !createRes.data.error) {
                return props.sendQuote(createRes.data.quote_id, form.fk_email_recipient).then(() => {
                    props.setModalOpen(false);
                });
            }
        });
    });

    const handleSaveDraft = guardSaveDraft((e) => {
        e.preventDefault();
        return props.createQuote(buildQuotePayload()).then((createRes) => {
            if (createRes && createRes.data && !createRes.data.error) {
                props.setModalOpen(false);
                props.history.push(`/quote/view/${createRes.data.quote_id}`);
            }
        });
    });

    return (
        <Modal blurBack={true} hasCloseButton={true} hideLogo handleClose={() => props.setModalOpen(false)}>
            <div className="flex-column w100">
                <form onSubmit={handleSaveDraft} className="form">
                    <h3 className="title2 gap-below-m heavy t-center">Send a New Quote</h3>

                    <div className="inputs-wrapper gap-below-m">
                        <div className="input-basic">
                            <input type="text" name="name" id="quote_name" value={form.name} onChange={handleChange} required />
                            <label htmlFor="quote_name">Quote Name <span>*</span></label>
                        </div>
                        <div className="input-basic">
                            <input type="email" name="fk_email_recipient" id="quote_recipient" value={form.fk_email_recipient} onChange={handleChange} />
                            <label htmlFor="quote_recipient">Recipient Email</label>
                        </div>
                        <div className="input-basic">
                            <input type="number" step="0.01" name="contract_value" id="quote_value" value={form.contract_value} onChange={handleChange} />
                            <label htmlFor="quote_value">Estimated Value</label>
                        </div>
                        <div className="input-basic">
                            <input type="date" name="start_date" id="quote_start_date" value={form.start_date} onChange={handleChange} />
                            <label htmlFor="quote_start_date">Proposed Start Date</label>
                        </div>
                        <div className="input-basic">
                            <input type="date" name="completion_date" id="quote_completion_date" value={form.completion_date} onChange={handleChange} />
                            <label htmlFor="quote_completion_date">Proposed Completion Date</label>
                        </div>
                        <div className="input-basic textarea addspace">
                            <label className="textarea" htmlFor="quote_description">Description</label>
                            <textarea name="description" id="quote_description" rows="3" value={form.description} onChange={handleChange} />
                        </div>
                    </div>

                    {/* GitHub #663: pre-consent, up front, to let the Purchaser skip straight to a
                        real Contract at Accept time -- avoids a redundant later "Convert to Contract"
                        round trip for simple engagements where the Quote just IS the Contract */}
                    <div className="checkbox-wrapper gap-below-m" onClick={() => setAllowConversion(!allowConversion)}>
                        <input
                            className="typical-checkbox"
                            type="checkbox"
                            id="allow_contract_conversion"
                            checked={allowConversion}
                            onChange={() => setAllowConversion(!allowConversion)}
                        />
                        <label htmlFor="allow_contract_conversion" className="content2 light-black multiline">
                            Allow contract conversion -- let the recipient Accept and convert this straight to a Contract in one step
                        </label>
                    </div>
                    {allowConversion &&
                    <div className="inputs-wrapper gap-below-m">
                        <div className="input-basic">
                            <select id="quote_conversion_project" value={conversionPid} onChange={(e) => setConversionPid(e.target.value)} required>
                                <option value="">Select a project</option>
                                {company_projects && company_projects.map((project) => (
                                    <option key={project.project_id} value={project.project_id}>{project.project_name}</option>
                                ))}
                            </select>
                            <label htmlFor="quote_conversion_project">Attach the resulting Contract to <span>*</span></label>
                        </div>
                    </div>}

                    <div className="flex-row">
                        <button id="send_quote" className="btn pri min-width gap-right-xs" type="button" onClick={handleSend} disabled={disableSend}>{isSending ? "Sending..." : "Send Quote"}</button>
                        <button id="save_quote_draft" className="btn sec min-width gap-right-xs" type="submit" disabled={disableSaveDraft}>{isSavingDraft ? "Saving..." : "Save as Draft"}</button>
                        <button
                            className="btn tb min-width"
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                props.setModalOpen(false);
                            }}>Cancel
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps, {createQuote, sendQuote, getCompanyPojects})(QuoteCreateModal);
